import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { BookOpen, Search, Rss, Star, MessageSquare, Users, ArrowLeftRight, Home, Map, Trophy } from "lucide-react";

const live = [
  { icon: BookOpen, label: "Your Shelf", desc: "Track what you're reading, have read, and want to read." },
  { icon: Search, label: "Book Discovery", desc: "Search millions of titles. Add anything to your shelf instantly." },
  { icon: Rss, label: "Activity Feed", desc: "See what your network is reading, reviewing, and discussing." },
  { icon: Star, label: "Reviews & Ratings", desc: "Share your take. Leave honest reviews other readers can trust." },
  { icon: MessageSquare, label: "Discussions", desc: "Open conversations around any book — spoilers welcome." },
  { icon: Users, label: "Reader Profiles", desc: "Build your reader identity. Follow people whose taste you trust." },
];

const coming = [
  { icon: ArrowLeftRight, label: "Book Swaps", desc: "Trade titles directly with readers nearby." },
  { icon: Home, label: "Book Marketplace", desc: "Buy and sell books within the Lagos community." },
  { icon: Map, label: "Local Discovery", desc: "Find readers in your neighbourhood." },
  { icon: Trophy, label: "Reading Challenges", desc: "Set goals, track streaks, celebrate milestones." },
];

export default function BetaStatusSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="beta-status"
      data-testid="beta-status-section"
      className="py-24 sm:py-32 bg-ink"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="font-body text-sm uppercase tracking-[0.25em] text-terra mb-4">
            Private Beta · 2026
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-cream mb-4">
            What you're getting<br className="hidden sm:block" /> access to
          </h2>
          <p className="font-body text-base text-cream/50 max-w-md leading-relaxed">
            You're among the first. Here's what's live today — and where we're headed.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Live now */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="w-2 h-2 rounded-full bg-forest animate-pulse" />
              <p className="font-body text-sm font-semibold uppercase tracking-widest text-cream/60">
                Live Now
              </p>
            </div>
            <div className="space-y-6">
              {live.map(({ icon: Icon, label, desc }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.07 }}
                  className="flex items-start gap-4"
                >
                  <div className="mt-0.5 w-9 h-9 rounded-lg bg-forest/20 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-forest" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-body text-sm font-semibold text-cream mb-0.5">{label}</p>
                    <p className="font-body text-sm text-cream/50 leading-relaxed">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Coming next */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="w-2 h-2 rounded-full bg-terra/70" />
              <p className="font-body text-sm font-semibold uppercase tracking-widest text-cream/60">
                Coming Next
              </p>
            </div>
            <div className="space-y-6">
              {coming.map(({ icon: Icon, label, desc }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.07 }}
                  className="flex items-start gap-4 opacity-60"
                >
                  <div className="mt-0.5 w-9 h-9 rounded-lg bg-cream/5 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-terra/80" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-body text-sm font-semibold text-cream mb-0.5">{label}</p>
                    <p className="font-body text-sm text-cream/50 leading-relaxed">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Closing note */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="font-body text-sm text-cream/30 italic mt-12 leading-relaxed border-t border-cream/10 pt-8"
            >
              We're building this with our beta community. Your feedback shapes what ships next.
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
